import React, { useCallback, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Tip,
  WidgetInfo,
  WidgetSkeleton,
  formatCurrency,
  fullCurrency,
  fullNumber,
  num,
  pct,
  pick,
} from "./skdShared";

export const normalizeFleetAndOverdue = (data = []) => {
  const list = Array.isArray(data)
    ? data
    : pick(data, "clients", "clientList", "accounts", "items") || [];

  const clients = list.map((item) => ({
    accountName:
      pick(item, "accountName", "clientName", "contractNumber", "name") || "—",
    clientName: item.contractNumber || pick(item, "clientName", "name") || "—",
    fleetSize: num(item.totalVehicles),
    newVehicles: 0,
    throughput:
      num(item.totalVehicles) > 0
        ? num(item.servedVehicles / item.totalVehicles) * 100
        : 0,
    outstanding: num(item.outstanding),
    overdue: num(item.overDue),
    totalPaid: num(item.totalPaid),
    amountCredited: num(item.amountCredited),
    servedVehicles: num(item.servedVehicles),
  }));

  const totals = clients.reduce(
    (acc, item) => ({
      totalOutstanding: acc.totalOutstanding + item.outstanding,
      totalOverdue: acc.totalOverdue + item.overdue,
      totalCollected: acc.totalCollected + item.totalPaid,
      totalCredited: acc.totalCredited + item.amountCredited,
    }),
    {
      totalOutstanding: 0,
      totalOverdue: 0,
      totalCollected: 0,
      totalCredited: 0,
    },
  );

  return {
    ...totals,
    collected: totals.totalCollected,
    outstanding: totals.totalOutstanding,
    overdue: totals.totalOverdue,
    clients,
  };
};

const sortClientDirectory = (list, { column, direction }) => {
  const factor = direction === "asc" ? 1 : -1;

  return [...list].sort((a, b) => {
    switch (column) {
      case "accountName": {
        const aName = (a.accountName || "").toLowerCase();
        const bName = (b.accountName || "").toLowerCase();
        return factor * aName.localeCompare(bName);
      }
      case "fleetSize":
        return factor * (a.fleetSize - b.fleetSize);
      case "throughput":
        return factor * (a.throughput - b.throughput);
      case "outstanding":
        return factor * (a.outstanding - b.outstanding);
      case "overdue":
        return factor * (a.overdue - b.overdue);
      default:
        return 0;
    }
  });
};

const ClientDirectoryWidget = ({
  loading,
  data,
  currency = "AED",
  t,
  clientFilter = "all",
}) => {
  const [clientSearch, setClientSearch] = useState("");
  const [clientSort, setClientSort] = useState({
    column: "overdue",
    direction: "desc",
  });

  const handleClientSortSelect = useCallback((event) => {
    const column = event.target.value;
    setClientSort({
      column,
      direction: column === "accountName" ? "asc" : "desc",
    });
  }, []);

  const clientDirectoryBase = useMemo(() => {
    const overdueData = normalizeFleetAndOverdue(data);
    let list = overdueData.clients || [];
    if (clientFilter === "overdue") {
      list = list.filter((c) => c.overdue > 0);
    } else if (clientFilter === "outstanding") {
      list = list.filter((c) => c.outstanding > 0);
    }
    return {
      clients: list,
      totalOutstanding: list.reduce((sum, c) => sum + (c.outstanding || 0), 0),
      totalOverdue: list.reduce((sum, c) => sum + (c.overdue || 0), 0),
    };
  }, [data, clientFilter]);

  const filteredClients = useMemo(() => {
    let list = clientDirectoryBase.clients || [];
    const q = clientSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        (c.accountName || c.clientName || "").toLowerCase().includes(q),
      );
    }
    return sortClientDirectory(list, clientSort);
  }, [clientDirectoryBase, clientSearch, clientSort]);

  if (loading) {
    return <WidgetSkeleton height={400} />;
  }

  return (
    <div className="skd-widget skd-widget--client-directory">
      <header className="skd-mcd__head">
        <h3 className="skd-mcd__title">{t("skdMasterClientDirectory")}</h3>
        <WidgetInfo title={t("skdMasterClientDirectoryInfo")} />
      </header>

      <div className="skd-mcd__tot">
        <div>
          <small>{t("skdClients")}</small>
          <b>{clientDirectoryBase.clients.length}</b>
        </div>
        <div>
          <small>{t("skdOutstanding")}</small>
          <Tip
            title={fullCurrency(clientDirectoryBase.totalOutstanding, currency)}
            className="skd-mcd__tot-val skd-mcd__tot-val--out"
          >
            {formatCurrency(clientDirectoryBase.totalOutstanding, currency)}
          </Tip>
        </div>
        <div>
          <small>{t("skdOverdue")}</small>
          <Tip
            title={fullCurrency(clientDirectoryBase.totalOverdue, currency)}
            className="skd-mcd__tot-val skd-mcd__tot-val--od"
          >
            {formatCurrency(clientDirectoryBase.totalOverdue, currency)}
          </Tip>
        </div>
      </div>

      <div className="skd-mcd__tools">
        <label className="skd-mcd__search">
          <SearchIcon className="skd-mcd__search-icon" fontSize="small" />
          <input
            type="search"
            placeholder={t("skdSearchClient")}
            aria-label={t("skdSearchClient")}
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
          />
        </label>
        <select
          className="skd-mcd__sort"
          aria-label={t("skdSortBy")}
          value={clientSort.column}
          onChange={handleClientSortSelect}
        >
          <option value="overdue">{t("skdSortOverdue")}</option>
          <option value="outstanding">{t("skdSortOutstanding")}</option>
          <option value="fleetSize">{t("skdSortFleetSize")}</option>
          <option value="throughput">{t("skdSortPenetration")}</option>
          <option value="accountName">{t("skdSortName")}</option>
        </select>
      </div>

      <ul className="skd-mcd__list skd-scroll-area">
        {filteredClients.map((client, idx) => {
          const overdueShare = client.outstanding
            ? Math.min((client.overdue / client.outstanding) * 100, 100)
            : 0;
          const penetrationHigh = client.throughput > 100;
          return (
            <li
              className="skd-mcd__row"
              key={`${client.accountName}-${idx}`}
              title={client.accountName}
            >
              <span className="skd-mcd__name">{client.accountName}</span>
              <Tip
                title={fullCurrency(client.overdue, currency)}
                className="skd-mcd__od"
              >
                {t("skdOverdueShort")}{" "}
                <b>{formatCurrency(client.overdue, currency)}</b>
              </Tip>
              <span className="skd-mcd__meta">
                <span className="skd-mcd__fl">
                  {client.fleetSize.toLocaleString()}{" "}
                  {client.fleetSize === 1 ? t("skdVehicle") : t("skdVehicles")}
                </span>
                <i className="skd-mcd__sep" aria-hidden="true" />
                <Tip
                  title={
                    penetrationHigh
                      ? `${t("skdThroughput")}: ${fullNumber(client.throughput, 2)}%\n${t("skdPenetrationOver100")}`
                      : `${t("skdThroughput")}: ${fullNumber(client.throughput, 2)}%`
                  }
                  className={`skd-mcd__pn${penetrationHigh ? " skd-mcd__pn--hi" : ""}`}
                >
                  {client.throughput.toFixed(1)}%
                  {penetrationHigh ? " ⚠" : ""}
                </Tip>
              </span>
              <Tip
                title={fullCurrency(client.outstanding, currency)}
                className="skd-mcd__os"
              >
                {t("skdOutstShort")}{" "}
                <b>{formatCurrency(client.outstanding, currency)}</b>
              </Tip>
              <span
                className="skd-mcd__bar"
                title={`${overdueShare.toFixed(0)}% ${t("skdOverdueShareOfOutstanding")}`}
              >
                <i style={{ width: `${overdueShare}%` }} />
              </span>
            </li>
          );
        })}
        {!filteredClients.length && (
          <li className="skd-mcd__empty">
            {clientSearch.trim()
              ? t("skdNoClientMatch").replace("{{q}}", clientSearch.trim())
              : t("skdNoData")}
          </li>
        )}
      </ul>

      <div className="skd-mcd__foot">
        <span>
          {t("skdClientsCount")
            .replace("{{shown}}", String(filteredClients.length))
            .replace("{{total}}", String(clientDirectoryBase.clients.length))}
        </span>
        <span>{t("skdClientDirectoryFoot")}</span>
      </div>
    </div>
  );
};

export default ClientDirectoryWidget;
